import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessReturnForm = ({ projectMode = false }) => {
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoice_id');

  const [invoice, setInvoice] = useState(null);
  const [returnableItems, setReturnableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [typeRemboursement, setTypeRemboursement] = useState('credit_client');
  const [methodeRemboursement, setMethodeRemboursement] = useState('especes');
  const [motif, setMotif] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoiceId) {
      navigate(`${routeBase}/invoices`);
      return;
    }
    loadData();
  }, [invoiceId]);

  const loadData = async () => {
    try {
      const [invoiceRes, itemsRes] = await Promise.all([
        api.get(`${apiBase}/invoices/${invoiceId}`),
        api.get(`${apiBase}/invoices/${invoiceId}/returnable-items`)
      ]);
      setInvoice(invoiceRes.data);
      const items = itemsRes.data.filter(i => i.quantite_retournable > 0);
      setReturnableItems(items);
      setSelectedItems(items.map(i => ({
        invoice_item_id: i.id,
        product_id: i.product_id,
        description: i.description,
        prix_unitaire: parseFloat(i.prix_unitaire),
        quantite_max: i.quantite_retournable,
        quantite_retournee: 0,
        selected: false
      })));
    } catch (err) {
      console.error(err);
      alert('Erreur lors du chargement');
      navigate(`${routeBase}/invoices`);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (index) => {
    setSelectedItems(prev => prev.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          selected: !item.selected,
          quantite_retournee: !item.selected ? item.quantite_max : 0
        };
      }
      return item;
    }));
  };

  const updateQuantity = (index, qty) => {
    const val = Math.max(0, Math.min(parseInt(qty) || 0, selectedItems[index].quantite_max));
    setSelectedItems(prev => prev.map((item, i) => {
      if (i === index) return { ...item, quantite_retournee: val, selected: val > 0 };
      return item;
    }));
  };

  const getTotal = () => {
    return selectedItems
      .filter(i => i.selected && i.quantite_retournee > 0)
      .reduce((sum, i) => sum + (i.quantite_retournee * i.prix_unitaire), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = selectedItems.filter(i => i.selected && i.quantite_retournee > 0);
    if (items.length === 0) {
      alert('Sélectionnez au moins un article à retourner');
      return;
    }

    const total = getTotal();
    if (!window.confirm(`Confirmer le retour de ${items.length} article(s) pour un total de ${total.toLocaleString()} XOF ?\n\nLe stock sera réintégré automatiquement.`)) return;

    setSaving(true);
    try {
      const res = await api.post(`${apiBase}/returns`, {
        invoice_id: parseInt(invoiceId),
        client_id: invoice.client_id,
        type_remboursement: typeRemboursement,
        methode_remboursement: methodeRemboursement,
        motif,
        notes,
        sous_total: total,
        total: total,
        items: items.map(i => ({
          invoice_item_id: i.invoice_item_id,
          product_id: i.product_id,
          description: i.description,
          quantite_retournee: i.quantite_retournee,
          prix_unitaire: i.prix_unitaire,
          total: i.quantite_retournee * i.prix_unitaire
        }))
      });
      navigate(`${routeBase}/returns/${res.data.id}`);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la création du retour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (returnableItems.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <span className="text-6xl mb-4 block">✅</span>
          <h3 className="text-xl font-bold text-gray-700 mb-2">Tous les articles ont déjà été retournés</h3>
          <p className="text-gray-500 mb-6">Il n'y a plus d'articles retournables sur cette facture.</p>
          <button onClick={() => navigate(`${routeBase}/invoices/${invoiceId}`)} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
            Retour à la facture
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate(`${routeBase}/invoices/${invoiceId}`)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Retour de produits</h1>
        <p className="text-gray-500">Facture {invoice?.numero_facture} • Client : {invoice?.client_nom || 'Sans client'}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Articles à retourner */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Articles à retourner</h2>
          <div className="space-y-3">
            {selectedItems.map((item, index) => (
              <div key={index} className={`p-4 rounded-xl border-2 transition-all ${item.selected ? 'border-red-300 bg-red-50' : 'border-gray-100 hover:border-gray-200'}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={() => toggleItem(index)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-800">{item.description}</span>
                      <span className="text-sm text-gray-500">{item.prix_unitaire.toLocaleString()} XOF/unité</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      Retournable : {item.quantite_max} unité(s)
                    </div>
                    {item.selected && (
                      <div className="mt-3 flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-600">Quantité à retourner :</label>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => updateQuantity(index, item.quantite_retournee - 1)}
                            className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold">−</button>
                          <input
                            type="number"
                            value={item.quantite_retournee}
                            onChange={(e) => updateQuantity(index, e.target.value)}
                            min="1"
                            max={item.quantite_max}
                            className="w-16 text-center border rounded-lg py-1 focus:ring-2 focus:ring-red-500"
                          />
                          <button type="button" onClick={() => updateQuantity(index, item.quantite_retournee + 1)}
                            className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold">+</button>
                        </div>
                        <span className="text-sm font-bold text-red-600 ml-auto">
                          {(item.quantite_retournee * item.prix_unitaire).toLocaleString()} XOF
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Options du retour</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de remboursement</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button"
                  onClick={() => setTypeRemboursement('credit_client')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${typeRemboursement === 'credit_client' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="font-medium">💰 Crédit client</div>
                  <div className="text-sm text-gray-500 mt-1">Le montant est crédité sur le solde du client</div>
                </button>
                <button type="button"
                  onClick={() => setTypeRemboursement('remboursement_direct')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${typeRemboursement === 'remboursement_direct' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="font-medium">🔄 Remboursement direct</div>
                  <div className="text-sm text-gray-500 mt-1">Le client est remboursé directement</div>
                </button>
              </div>
            </div>

            {typeRemboursement === 'remboursement_direct' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode de remboursement</label>
                <select value={methodeRemboursement} onChange={(e) => setMethodeRemboursement(e.target.value)} className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500">
                  <option value="especes">💵 Espèces</option>
                  <option value="mobile_money">📱 Mobile Money</option>
                  <option value="virement">🏦 Virement bancaire</option>
                  <option value="cheque">📄 Chèque</option>
                  <option value="carte">💳 Carte bancaire</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Un décaissement sera automatiquement enregistré dans la caisse</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motif du retour</label>
              <select value={motif} onChange={(e) => setMotif(e.target.value)} className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500">
                <option value="">Sélectionner un motif</option>
                <option value="Produit défectueux">Produit défectueux</option>
                <option value="Erreur de commande">Erreur de commande</option>
                <option value="Non conforme">Non conforme à la description</option>
                <option value="Excédent">Excédent de livraison</option>
                <option value="Annulation partielle">Annulation partielle</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Détails supplémentaires..."
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Total + Submit */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-bold text-gray-800">Total de l'avoir</span>
            <span className="text-2xl font-bold text-red-600">{getTotal().toLocaleString()} XOF</span>
          </div>
          <button
            type="submit"
            disabled={saving || selectedItems.filter(i => i.selected && i.quantite_retournee > 0).length === 0}
            className="w-full py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Création en cours...' : '📋 Créer l\'avoir'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessReturnForm;
